import { Component, OnInit, OnDestroy, ChangeDetectorRef, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  TaskService,
  WebSocketService,
  AuthService,
  DashboardLiveSyncService,
  DASHBOARD_POLLING_WS_DISCONNECTED_MS,
  DASHBOARD_SOCKET_DEBOUNCE_MS,
  DashboardSocketHandler,
} from '@org/shared-services';
import { Task, TaskCounts } from '@org/interfaces';
import { Subject } from 'rxjs';
import { takeUntil, debounceTime, take } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationDialogComponent } from '@org/components';
import { SysNotifyService } from '@org/shared-services';
import * as moment from 'moment';

@Component({
  selector: 'app-csr-view',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, ConfirmationDialogComponent],
  templateUrl: './csr-view.component.html',
  styleUrls: ['./csr-view.component.css']
})
export class CSRViewComponent implements OnInit, OnDestroy {
  tasks: Task[] = [];
  currentPage = 1;
  totalPages = 1;
  limit = 10;
  isLoading = false;
  activeTab: 'pending' | 'closed' = 'pending';
  private destroy$ = new Subject<void>();
  private pollingTickInterval: ReturnType<typeof setInterval> | null = null;
  private isWebSocketConnected = false;
  private readonly socketRefresh$ = new Subject<void>();
  private dashboardSocketHandlers: DashboardSocketHandler[] = [];
  private socketBindAttempts = { count: 0 };
  lastUpdateTime: Date = new Date();

  pendingRequestsCount = 0;
  closedRequestsCount = 0;
  overdueRequestsCount = 0;

  constructor(
    private taskService: TaskService,
    private cdr: ChangeDetectorRef,
    private dialog: MatDialog,
    private sysNotifyService: SysNotifyService,
    private ws: WebSocketService,
    private authService: AuthService,
    private liveSync: DashboardLiveSyncService
  ) {}

  ngOnInit(): void {
    this.ws.connect();
    this.ws.connectionStatus$.pipe(takeUntil(this.destroy$)).subscribe(isConnected => {
      this.isWebSocketConnected = isConnected;
    });

    this.taskService.totalTasksRead$.pipe(takeUntil(this.destroy$)).subscribe({
      next: (counts: TaskCounts) => {
        this.pendingRequestsCount = counts.csrPending || 0;
        this.closedRequestsCount = counts.outgoing || 0;
        this.overdueRequestsCount = counts.overDueTechRequests || 0;
        this.cdr.detectChanges();
      },
      error: (error: Error) => {
        console.error('Error loading task counts:', error);
        this.sysNotifyService.showError('Error loading task counts', 3000, 'right', 'top');
      }
    });

    this.authService.getCurrentUser().pipe(take(1), takeUntil(this.destroy$)).subscribe(() => {
      this.loadTasks();
      this.loadTaskCounts();
      this.setupDashboardSocketRefresh();
      this.setupAdaptivePolling();
    });

    this.socketRefresh$
      .pipe(debounceTime(DASHBOARD_SOCKET_DEBOUNCE_MS), takeUntil(this.destroy$))
      .subscribe(() => {
        this.loadTasks();
        this.loadTaskCounts();
        this.lastUpdateTime = new Date();
        this.cdr.markForCheck();
      });
  }

  ngOnDestroy(): void {
    this.liveSync.unbindTaskRefreshSocket(this.ws, this.dashboardSocketHandlers);
    if (this.pollingTickInterval) {
      clearInterval(this.pollingTickInterval);
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  private resolveTenant(): string | undefined {
    const u = this.authService.currentUserValue;
    return this.liveSync.resolveDashboardTenantId(u);
  }

  private setupDashboardSocketRefresh(): void {
    this.liveSync.bindTaskRefreshSocket(
      this.ws,
      this.dashboardSocketHandlers,
      this.socketBindAttempts,
      () => this.resolveTenant(),
      () => this.socketRefresh$.next()
    );
  }

  private setupAdaptivePolling(): void {
    this.pollingTickInterval = setInterval(() => {
      if (document.hidden) {
        return;
      }
      if (this.liveSync.shouldRunSafetyPoll(this.isWebSocketConnected, this.liveSync.lastSocketRefresh)) {
        this.refreshTasks(false);
      }
    }, DASHBOARD_POLLING_WS_DISCONNECTED_MS);
  }

  @HostListener('document:visibilitychange')
  onDocumentVisibilityChange(): void {
    if (document.visibilityState !== 'visible') {
      return;
    }
    if (!this.ws.isConnected()) {
      this.ws.connect();
      this.setupDashboardSocketRefresh();
    }
    this.refreshTasks(false);
  }

  loadTasks(): void {
    this.isLoading = true;
    const observable = this.activeTab === 'pending' 
      ? this.taskService.getCSRPendingTasks(this.currentPage, this.limit)
      : this.taskService.getCSRClosedTasks(this.currentPage, this.limit);

    observable.pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (data) => {
        this.tasks = data.tasks.map((task: Task) => ({
          ...task,
          timeSinceRequest: this.calculateTimeSinceRequest(task.createdAt),
          flag: this.calculateFlag(task.createdAt, task.taskCompleted)
        }));
        this.totalPages = data.totalPages;
        this.isLoading = false;
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('Error loading tasks:', error);
        this.sysNotifyService.showError('Error loading tasks', 3000, 'right', 'top');
        this.isLoading = false;
        this.cdr.detectChanges();
      }
    });
  }

  loadTaskCounts(): void {
    this.taskService.getTaskCounts();
  }

  refreshTasks(showToast = true): void {
    this.currentPage = 1;
    this.loadTasks();
    this.loadTaskCounts();
    this.lastUpdateTime = new Date();
    if (showToast) {
      this.sysNotifyService.showSuccess('Tasks refreshed', 2000, 'right', 'top');
    }
  }

  switchTab(tab: 'pending' | 'closed'): void {
    if (this.activeTab !== tab) {
      this.activeTab = tab;
      this.currentPage = 1;
      this.loadTasks();
    }
  }

  closeTask(task: Task): void {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '350px',
      data: {
        message: 'Are you sure you want to close this task?',
        btn: 'Close Task',
        task: task
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === true) {
        this.taskService.csrCloseTask(task._id!, { closedBy: 'csr' }).subscribe({
          next: () => {
            this.sysNotifyService.showSuccess('Task closed successfully', 3000, 'right', 'top');
            this.loadTasks();
            this.loadTaskCounts();
          },
          error: (error) => {
            console.error('Error closing task:', error);
            this.sysNotifyService.showError('Error closing task', 3000, 'right', 'top');
          }
        });
      }
    });
  }

  nextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.loadTasks();
    }
  }

  previousPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.loadTasks();
    }
  }

  formatDate(date: Date | undefined): string {
    if (!date) return '';
    return moment(date).format('MM/DD/YY, h:mm A');
  }

  calculateTimeSinceRequest(start: Date | undefined): string {
    if (!start) return '';
    const now = moment();
    const duration = moment.duration(now.diff(moment(start)));
    const hours = Math.floor(duration.asHours()).toString().padStart(2, '0');
    const minutes = duration.minutes().toString().padStart(2, '0');
    const seconds = duration.seconds().toString().padStart(2, '0');
    return `${hours}:${minutes}:${seconds}`;
  }

  calculateFlag(createdAt: Date | undefined, taskCompleted: boolean): string {
    if (taskCompleted) {
      return 'green';
    }
    const now = moment();
    const durationInMinutes = moment.duration(now.diff(moment(createdAt))).asMinutes();
    if (durationInMinutes > 30) {
      return 'red';
    } else if (durationInMinutes > 15) {
      return 'yellow';
    } else {
      return 'white';
    }
  }

  getStatusClass(task: Task): string {
    if (task.taskCompleted) {
      return 'bg-green-100 text-green-800';
    }
    const flag = this.calculateFlag(task.createdAt, task.taskCompleted);
    switch (flag) {
      case 'red':
        return 'bg-red-100 text-red-800';
      case 'yellow':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  }
}
